import { t, type AmsMessageKey } from '../../messages.ts';

/**
 * Integrity → Audit chain → Records checked: the pure part of the popup that
 * lists the records an administrator should know about
 * (GET /v1/platform/audit/records — the API says which and why). Kept out of
 * the .tsx so a test can read it without a DOM.
 */

export const AUDIT_RECORD_KINDS = ['direct_edit', 'deletion', 'access', 'login_as', 'standing', 'money'] as const;
export type AuditRecordKind = (typeof AUDIT_RECORD_KINDS)[number];
export type AuditRecordFilter = 'all' | AuditRecordKind;

export interface AuditRecordRow {
  readonly id: string;
  readonly kind: AuditRecordKind;
  readonly action: string;
  readonly action_kind: string;
  readonly at: string;
  readonly chain_key: string;
  readonly seq: string;
  /** null is the system: a deadline or a job, no person. */
  readonly actor: { readonly id: string; readonly name: string | null; readonly email: string | null } | null;
  readonly actor_role: string;
  readonly operator_id: string | null;
  readonly operator: string | null;
  readonly entity_type: string;
  readonly entity_id: string | null;
  readonly reason: string | null;
}

export interface AuditRecordPage {
  readonly items: readonly AuditRecordRow[];
  readonly next_before: string | null;
}

const KIND_KEYS: Readonly<Record<AuditRecordKind, AmsMessageKey>> = {
  direct_edit: 'integrity.records.kind.direct_edit',
  deletion: 'integrity.records.kind.deletion',
  access: 'integrity.records.kind.access',
  login_as: 'integrity.records.kind.login_as',
  standing: 'integrity.records.kind.standing',
  money: 'integrity.records.kind.money',
};

export function recordKindLabel(kind: AuditRecordKind): string {
  return t(KIND_KEYS[kind]);
}

/** Each action this list can carry, in words. Deletions read as the Access log reads them. */
const ACTION_KEYS: Readonly<Record<string, AmsMessageKey>> = {
  'platform.direct_edit': 'integrity.accessLog.code.platform.direct_edit',
  'operator.soft_delete': 'integrity.accessLog.code.operator.soft_delete',
  'operator.restore': 'integrity.accessLog.code.operator.restore',
  'operator.purge': 'integrity.accessLog.code.operator.purge',
  'brand.soft_delete': 'integrity.accessLog.code.brand.soft_delete',
  'brand.restore': 'integrity.accessLog.code.brand.restore',
  'brand.purge': 'integrity.accessLog.code.brand.purge',
  'affiliate.soft_delete': 'integrity.accessLog.code.affiliate.soft_delete',
  'affiliate.restore': 'integrity.accessLog.code.affiliate.restore',
  'affiliate.purge': 'integrity.accessLog.code.affiliate.purge',
  'period.purge': 'integrity.records.action.period.purge',
  'period.discard': 'integrity.records.action.period.discard',
  'platform.capabilities.role': 'integrity.records.action.platform.capabilities.role',
  'platform.capabilities.user': 'integrity.records.action.platform.capabilities.user',
  'staff.add': 'integrity.records.action.staff.add',
  'staff.edit': 'integrity.records.action.staff.edit',
  'staff.remove': 'integrity.records.action.staff.remove',
  'staff.purge': 'integrity.records.action.staff.purge',
  'staff.mfa_reset': 'integrity.records.action.staff.mfa_reset',
  'platform.login_as.start': 'integrity.records.action.platform.login_as.start',
  'platform.login_as.end': 'integrity.records.action.platform.login_as.end',
  'operator.approve': 'integrity.records.action.operator.approve',
  'operator.reject': 'integrity.records.action.operator.reject',
  'operator.suspend': 'integrity.records.action.operator.suspend',
  'operator.reactivate': 'integrity.records.action.operator.reactivate',
  'identity.suspend': 'integrity.records.action.identity.suspend',
  'identity.reactivate': 'integrity.records.action.identity.reactivate',
  'identity.close': 'integrity.records.action.identity.close',
  RESTATEMENT_REQUESTED: 'integrity.records.action.RESTATEMENT_REQUESTED',
  RESTATEMENT_APPROVED: 'integrity.records.action.RESTATEMENT_APPROVED',
  RESTATEMENT_REJECTED: 'integrity.records.action.RESTATEMENT_REJECTED',
  PERIOD_LOCKED: 'integrity.records.action.PERIOD_LOCKED',
  CREDIT_NOTE_ISSUED: 'integrity.records.action.CREDIT_NOTE_ISSUED',
  'invoice.write_off': 'integrity.records.action.invoice.write_off',
};

/**
 * The action in words. A record a direct edit wrote can be any route's own
 * action, so an unknown one keeps its code — searchable, not guessed at.
 */
export function recordActionWords(row: Pick<AuditRecordRow, 'action' | 'kind'>): string {
  const key = ACTION_KEYS[row.action];
  if (key) return t(key);
  if (row.kind === 'direct_edit') return t('integrity.accessLog.code.editedRecord', { action: row.action });
  return row.action;
}

/** Who acted: a name, else the email, else the system (a deadline or a job). */
export function recordActor(row: Pick<AuditRecordRow, 'actor'>): string {
  if (!row.actor) return t('integrity.records.system');
  return row.actor.name?.trim() || row.actor.email || row.actor.id;
}

/** "chain · #seq", the record's place as the verification would name it. */
export function chainPosition(row: Pick<AuditRecordRow, 'chain_key' | 'seq'>): string {
  return `${row.chain_key} · #${row.seq}`;
}

export function recordsQuery(f: { kind: AuditRecordFilter; before?: string | null }): Record<string, string> {
  return {
    ...(f.kind !== 'all' ? { kind: f.kind } : {}),
    ...(f.before ? { before: f.before } : {}),
  };
}
